import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DateRange } from 'react-day-picker'

import { DateRangePicker } from '@/components/shared/date-range-picker'
import { ListPagination } from '@/components/shared/list-pagination'
import { useGetDrivers } from '@/features/drivers/hooks/use-get-drivers'
import { DddFilesDownloadBar } from '@/features/tachograph/components/ddd-files-download-bar'
import { DddFilesTable } from '@/features/tachograph/components/ddd-files-table'
import {
  useDownloadDddReport,
  useDownloadFiles,
  useGetDriverFiles,
} from '@/features/tachograph/hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute(
  '/dashboard/tachograph-activities/driver/$driverId',
)({
  head: () => ({ meta: [{ title: localizedPageTitle('driverActivity') }] }),
  component: DriverActivityPage,
})

function DriverActivityPage() {
  useDocumentTitle('driverActivity')
  const { t } = useTranslation('tachographActivities')
  const { driverId } = Route.useParams()
  const numericId = Number(driverId)

  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>([])
  const [downloadRange, setDownloadRange] = useState<DateRange | undefined>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const { data: drivers } = useGetDrivers()
  const driver = drivers?.find((d) => d.id === numericId)

  const { data: filesData, isLoading: filesLoading } = useGetDriverFiles({
    pageNumber: page,
    pageSize,
    driverId: numericId,
    from: downloadRange?.from?.toISOString(),
    to: downloadRange?.to?.toISOString(),
  })

  const downloadMutation = useDownloadFiles()
  const reportMutation = useDownloadDddReport()

  const handleDownloadReport = (id: string) => {
    reportMutation.mutate({ type: 'driver', id })
  }

  const handleDownloadSingle = (id: string) => {
    downloadMutation.mutate({ type: 'driver', ids: [id] })
  }

  const handleDownloadSelected = () => {
    if (selectedFileIds.length > 0) {
      downloadMutation.mutate({
        type: 'driver',
        ids: selectedFileIds,
      })
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link
            to="/dashboard/tachograph-activities"
            search={{ tab: 'driver' }}
          >
            <ArrowLeft className="size-4" />
          </Link>
        </Button>

        <div className="flex items-center gap-4">
          <div className="bg-background flex size-12 items-center justify-center rounded-md border">
            <User className="text-primary size-5" />
          </div>
          {driver ? (
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold">
                  {driver.firstName} {driver.lastName}
                </h1>
                <Badge variant="outline" className="text-[10px]">
                  {t(`driverStatus.${driver.status}`)}
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                {driver.tachographCardId}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-48" />
            </div>
          )}
        </div>
      </div>

      {/* DDD Files */}
      <Card className="max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
        <CardHeader className="max-md:px-0">
          <CardTitle className="text-sm">{t('tachographFiles')}</CardTitle>
        </CardHeader>
        <CardContent className="max-md:px-0">
          {/* Files filters — download date range for drivers */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-xs">
              {t('filesFilter.downloadDate')}:
            </span>
            <DateRangePicker
              value={downloadRange}
              onChange={setDownloadRange}
              placeholder={t('filesFilter.downloadDate')}
              className="h-8 text-xs"
            />
          </div>

          <DddFilesTable
            type="driver"
            items={filesData?.items ?? []}
            isLoading={filesLoading}
            onDownload={handleDownloadSingle}
            onDownloadReport={handleDownloadReport}
            reportPendingId={
              reportMutation.isPending
                ? (reportMutation.variables?.id ?? null)
                : null
            }
            selectedIds={selectedFileIds}
            onSelectionChange={setSelectedFileIds}
          />

          <ListPagination
            page={filesData?.page ?? 1}
            totalPages={filesData?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={filesData?.totalCount ?? 0}
            hasPrevious={filesData?.hasPrevious ?? false}
            hasNext={filesData?.hasNext ?? false}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(1)
            }}
            className="mt-4 px-4 max-md:px-0"
          />

          <DddFilesDownloadBar
            selectedCount={selectedFileIds.length}
            onDownload={handleDownloadSelected}
            onClear={() => setSelectedFileIds([])}
            isDownloading={downloadMutation.isPending}
            visible={selectedFileIds.length > 0}
          />
        </CardContent>
      </Card>
    </div>
  )
}
